import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, of } from 'rxjs';
import { getCentralisationInsurerLabel } from 'src/app/_enums/centralisation-insurer.enum';
import {
  getInsuranceTypeLabel,
  InsuranceType,
} from 'src/app/_enums/insurance-type.enum';
import { ProductService } from 'src/app/_services/product.service';
import { CentralisationCompany } from 'src/app/_types/centralisation.types';
import { CentralisationMetadata } from 'src/app/_types/lead.types';

import { LeadFormBase } from '../lead-form-base';

const LEGACY_VALUE_PREFIX = 'legacy:';

@Component({
  selector: 'app-lead-centralisation-form',
  templateUrl: './centralisation-form.component.html',
  styleUrls: ['./centralisation-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatSelectModule,
    MatFormFieldModule,
  ],
})
export class CentralisationFormComponent extends LeadFormBase {
  @Input()
  formGroup!: FormGroup;

  @Input()
  isCompany = false;

  InsuranceTypes = [
    InsuranceType.HEALTH,
    InsuranceType.THIRD_PILLAR,
    InsuranceType.BENEFITS_ACCOUNT,
    InsuranceType.COMPLEMENTARY_HEALTH,
    InsuranceType.RC_HOUSEHOLD,
    InsuranceType.BUILDING,
    InsuranceType.CONSTRUCTION,
    InsuranceType.MOTOR_VEHICLE,
    InsuranceType.TRAVEL,
    InsuranceType.LEGAL_PROTECTION,
    InsuranceType.HOUSING_RENTAL,
    InsuranceType.VALUABLE_OBJECTS,
    InsuranceType.ANIMAL,
    InsuranceType.ACCIDENT,
    InsuranceType.HEALTH_ALLOWANCE,
  ];

  BusinessInsurances = [
    InsuranceType.BUSINESS_RC_COMPANY,
    InsuranceType.BUSINESS_ACCIDENT,
    InsuranceType.BUSINESS_RETIREMENT,
    InsuranceType.BUSINESS_BUILDING,
    InsuranceType.BUSINESS_CONSTRUCTION,
    InsuranceType.BUSINESS_HEALTH,
    InsuranceType.BUSINESS_COMPANY_THINGS,
    InsuranceType.BUSINESS_MOTOR_VEHICLE,
    InsuranceType.BUSINESS_CYBERSECURITY,
    InsuranceType.BUSINESS_LEGAL_PROTECTION,
    InsuranceType.BUSINESS_FLEET,
    InsuranceType.BUSINESS_TRANSPORT,
    InsuranceType.BUSINESS_TECHNICAL_INSTALLATION,
  ];

  insurersInfo: FormGroup = this.fb.group({});

  metadataFormGroup: FormGroup = this.fb.group({
    type: this.fb.control(InsuranceType.CENTRALISATION),
    insurersInfos: this.fb.control([]),
    formChosenInsurers: this.insurersInfo,
  });

  allCentralisationCompanies: CentralisationCompany[] = [];

  legacyOptionsByInsurance: Partial<
    Record<InsuranceType, { value: string; label: string }[]>
  > = {};

  constructor(
    private fb: FormBuilder,
    private readonly productService: ProductService
  ) {
    super();
  }

  override handleSubmit(): Observable<boolean> {
    const insurers: { [key: string]: string[] } = this.insurersInfo.value;
    const mapping: { [key: string]: string[] } = {};
    Object.keys(insurers).forEach((insuranceType) => {
      insurers[insuranceType].forEach((value) => {
        if (value in mapping) {
          mapping[value].push(insuranceType);
        } else {
          mapping[value] = [insuranceType];
        }
      });
    });
    const result = Object.keys(mapping).map((value) =>
      value.startsWith(LEGACY_VALUE_PREFIX)
        ? {
            insurer: value.slice(LEGACY_VALUE_PREFIX.length),
            insurances: mapping[value],
          }
        : { companyId: value, insurances: mapping[value] }
    );
    this.metadataFormGroup.removeControl('formChosenInsurers');
    this.metadataFormGroup.controls['insurersInfos'].setValue(result);
    return of(true);
  }

  addInsurance(name: InsuranceType) {
    this.insurersInfo.addControl(
      name,
      this.fb.control([], [Validators.required])
    );
  }

  removeInsurance(name: InsuranceType) {
    this.insurersInfo.removeControl(name);
  }

  handleChange(insurance: InsuranceType, event: Event) {
    if ((event.target as HTMLInputElement).checked) {
      this.addInsurance(insurance);
      this.selectedInsurances.push(insurance);
    } else {
      this.removeInsurance(insurance);
      this.selectedInsurances = this.selectedInsurances.filter(
        (x) => x !== insurance
      );
    }
  }

  selectedInsurances: InsuranceType[] = [];

  getInsuranceTypeLabel = getInsuranceTypeLabel;

  getControl(name: string): FormControl {
    return this.insurersInfo.get(name) as FormControl;
  }

  // Cached per insurance type so *ngFor/mat-select in the template gets a
  // stable array/object reference on every change-detection cycle instead of
  // a freshly-allocated one — recomputing here caused mat-select to tear down
  // and rebuild its option list on every CD tick, freezing the page.
  private companyOptionsCache: Partial<
    Record<InsuranceType, { value: string; label: string }[]>
  > = {};

  getCompanyOptions(
    insurance: InsuranceType
  ): { value: string; label: string }[] {
    return (
      this.companyOptionsCache[insurance] ?? this.buildCompanyOptions(insurance)
    );
  }

  trackByOptionValue(
    _: number,
    option: { value: string; label: string }
  ): string {
    return option.value;
  }

  private buildCompanyOptions(
    insurance: InsuranceType
  ): { value: string; label: string }[] {
    const companyOptions = this.allCentralisationCompanies
      .filter((company) => company.insuranceTypes.includes(insurance))
      .map((company) => ({
        value: company.categoryId,
        label: company.categoryInFrench || company.category,
      }));
    const options = [
      ...companyOptions,
      ...(this.legacyOptionsByInsurance[insurance] ?? []),
    ].sort((a, b) => a.label.localeCompare(b.label));
    this.companyOptionsCache[insurance] = options;
    return options;
  }

  private rebuildAllCompanyOptions(): void {
    this.companyOptionsCache = {};
    [
      ...this.InsuranceTypes,
      ...this.BusinessInsurances,
      ...this.selectedInsurances,
    ].forEach((insurance) => this.buildCompanyOptions(insurance));
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');

    this.productService.getCentralisationCompanies({}).subscribe((res) => {
      this.allCentralisationCompanies = res.data.companies;
      this.rebuildAllCompanyOptions();
    });

    if (this.metadata) {
      const centralisationMetadata = this.metadata as CentralisationMetadata;
      // Reverse the mapping to have assurance -> [companyId/legacy values]
      // and add elements to insurersInfo group for each insurance type
      const insurersInfo: { [key: string]: string[] } = {};
      centralisationMetadata.insurersInfos.forEach((insurerInfo) => {
        const value =
          insurerInfo.companyId ??
          `${LEGACY_VALUE_PREFIX}${insurerInfo.insurer}`;
        insurerInfo.insurances.forEach((insurance) => {
          if (!this.selectedInsurances.includes(insurance as InsuranceType)) {
            this.selectedInsurances.push(insurance as InsuranceType);
            this.insurersInfo.addControl(
              insurance,
              this.fb.control([], [Validators.required])
            );
          }
          if (!insurerInfo.companyId && insurerInfo.insurer) {
            const existingLegacyOptions =
              this.legacyOptionsByInsurance[insurance as InsuranceType] ?? [];
            if (
              !existingLegacyOptions.some((option) => option.value === value)
            ) {
              this.legacyOptionsByInsurance[insurance as InsuranceType] = [
                ...existingLegacyOptions,
                {
                  value,
                  label: `${getCentralisationInsurerLabel(insurerInfo.insurer)} (historique)`,
                },
              ];
            }
          }
          if (insurersInfo[insurance]) {
            insurersInfo[insurance].push(value);
          } else {
            insurersInfo[insurance] = [value];
          }
        });
      });
      this.insurersInfo.patchValue(insurersInfo);
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);
    if (this.isCompany) this.InsuranceTypes = this.BusinessInsurances;
  }
}
